import styled from "styled-components";
import { UilAngleDown } from "@iconscout/react-unicons";
import { useCallback, useRef, useState } from "react";
import { TVariants } from ".";
import useDetectOutsideClick from "../../../hooks/useDetectOutsideClick";
import { handleGetDataFromLocalStorage } from "../../../utils/miscellaneous";
import { localStorageConstants } from "../../../utils/constants";
import { TOrganization } from "../../../redux/reducers/organizations";
import { useTypedSelector } from "../../../hooks/useTypedSelector";
import { clipWithEllipsis } from "../../../utils/formatters";
import Tag from "../Tag";

const Container = styled.div`
  position: relative;

  & > p {
    font-size: 10px;
    font-family: var(--font-paragraph-default);
    color: var(--color-aux-danger-700);

    position: absolute;
  }
`;

const Subcontainer = styled.button<{
  placeholder?: string;
  content: string;
  $onError: "hasError" | "noError";
  $variant: TVariants;
  $disabled?: boolean;
}>`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  min-height: 46px;
  font-family: var(--font-paragraph-default);

  /* Allow height to grow */
  height: var(--container-height);
  white-space: normal;

  background: ${({ $disabled }) =>
    $disabled ? `var(--color-light-100)` : `var(--color-light-0)`};

  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};

  border-radius: 8px;
  border: 1px solid
    ${({ $onError }) =>
      $onError === "hasError"
        ? `var(--color-aux-danger-700)`
        : `var(--color-dark-blue-300)`};

  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1);

  &::before {
    font-size: 9px;
    color: var(--color-text-labels);

    position: absolute;
    top: 8px;
    left: 12px;

    ${({ placeholder, content, $variant }) =>
      content?.length > 0 && $variant === "labeled"
        ? `
            content: '${placeholder}';
            visibility: visible;
          `
        : `content:'';
           visibility: hidden;
          `}
  }

  p {
    font-family: var(--font-paragraph-default);
    color: ${({ $disabled }) =>
      $disabled ? `var(--color-text-labels)` : ` var(--color-text-paragraph)`};
    font-size: 14px;

    position: absolute;
    left: 12px;
  }

  p.placeholder {
    color: var(--color-text-labels);
    top: 13px;
    ${({ content }) =>
      content?.length > 0 ? `visibility: hidden` : `visibility: visible`};

    & .mandatory {
      color: var(--color-aux-danger-700);
    }
  }

  p.content {
    top: 18px;
    ${({ content }) =>
      content?.length > 0 ? `visibility: visible` : `visibility: hidden`};
    width: calc(100% - 40px);
    text-align: start;
  }

  div.content {
    position: absolute;
    top: 18px;
    ${({ content }) =>
      content?.length > 0 ? `visibility: visible` : `visibility: hidden`};
    width: calc(100% - 40px);

    display: flex;
    flex-wrap: wrap;
    gap: 4px;

    span div {
      cursor: pointer !important;

      position: relative;
      margin-top: 2px;
      height: 14px;
      width: var(--label-width);

      svg {
        right: 1px;
        top: 9px;
        height: 7px;
        width: 7px;
      }
    }
  }

  div.naturalPadding {
    visibility: hidden;
    padding: 3px 0px;

    &::before {
      content: "${({ content }) => content}";
      display: block;
      width: calc(100% - 40px);
      font-size: 14px;
    }
  }

  svg {
    position: absolute;
    right: 12px;
    top: 12px;
  }

  svg.dropdown-arrow {
    transform: rotateZ(0deg);

    transition: transform 0.25s ease-in-out;
  }

  &:hover {
    background: ${({ $disabled }) => !$disabled && `var(--color-light-0)`};
  }

  &.active,
  &:active {
    border-color: var(--color-main-blue);

    svg {
      transform: rotateZ(180deg);
    }
  }
`;

type TCommonProps = {
  placeholder: string;
  multiSelect?: { labels: string[]; onRemove?: (tag: string) => void };
  onClick?: (
    setIsClicked: React.Dispatch<React.SetStateAction<boolean>>
  ) => void;
  onBlur?: (e: React.FocusEvent<HTMLButtonElement, Element>) => "show" | "hide";
  onError?: {
    hasError: boolean;
    message: string;
  };
  hasMandatoryStyle?: boolean;
  disabled?: boolean;
  label: string;
  componentRef?: React.MutableRefObject<HTMLDivElement | null>;
};

type TLabeled = {
  variant: "labeled";
};

type TUnlabeled = {
  variant: "unlabeled";
};

type TButtonProps<Variant extends TVariants> = Variant extends "labeled"
  ? TLabeled & TCommonProps
  : TUnlabeled & TCommonProps;

const Button: React.FC<TButtonProps<TVariants>> = ({
  variant,
  placeholder,
  label,
  onClick,
  multiSelect,
  onError,
  componentRef,
  disabled,
  hasMandatoryStyle,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [isClicked, setIsClicked] = useState(false);

  useDetectOutsideClick({
    isClicked: isClicked,
    componentRef,
    onOutside() {
      setIsClicked(false);
      if (onClick) onClick(setIsClicked);
    },
  });

  const selectedOrganization = useTypedSelector(
    (state) => state.organizations.selectedOrganization
  );

  const handleRenderButton = useCallback(() => {
    const textRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);

    switch (variant) {
      case "labeled":
        const localSelectedOrganization =
          selectedOrganization ??
          (handleGetDataFromLocalStorage(
            localStorageConstants.SELECT_ORGANIZATION
          ) as TOrganization);

        const handleOnClickButtonLabeled = (
          _e: React.MouseEvent<HTMLButtonElement, MouseEvent>
        ) => {
          setIsClicked((prev) => {
            return !prev;
          });
          if (onClick) onClick(setIsClicked);
        };

        return (
          <Container>
            <Subcontainer
              $disabled={disabled}
              ref={buttonRef}
              $variant={variant}
              $onError={(onError?.hasError && "hasError") || "noError"}
              role="button"
              onClick={handleOnClickButtonLabeled}
              className={!disabled && isClicked ? "active" : ""}
              placeholder={placeholder}
              content={clipWithEllipsis(label)}
              style={{
                ["--container-height" as any]:
                  (contentRef.current?.getBoundingClientRect().height ?? 0) +
                  24 +
                  "px",
              }}
            >
              {label?.length > 0 ? (
                <>
                  {multiSelect &&
                  Array.isArray(multiSelect.labels) &&
                  multiSelect.labels.length > 0 ? (
                    <div className="content" ref={contentRef}>
                      {multiSelect.labels.map((val: string) => (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (multiSelect.onRemove) {
                              multiSelect.onRemove(val);
                            }
                          }}
                          style={{
                            ["--label-width" as any]: `${val.length + 3}ch`,
                          }}
                        >
                          <Tag
                            className="item-multi"
                            key={val}
                            color="gray"
                            icon="customTimes"
                            iconPosition="right"
                            size="small"
                            text={val}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p ref={textRef} className="content">
                      {clipWithEllipsis(
                        placeholder.toLowerCase() === "organização"
                          ? localSelectedOrganization.company_name
                          : label
                      )}
                    </p>
                  )}
                  <div className="naturalPadding">
                    {clipWithEllipsis(
                      placeholder.toLowerCase() === "organização"
                        ? localSelectedOrganization.company_name
                        : label
                    )}
                  </div>
                </>
              ) : (
                <p className="placeholder">
                  {placeholder} {hasMandatoryStyle && <span>&nbsp;*</span>}
                </p>
              )}
              <UilAngleDown className="dropdown-arrow" />
            </Subcontainer>
            {onError && onError.hasError && <p>{onError.message}</p>}
          </Container>
        );
      case "unlabeled":
        const handleOnClickButtonUnlabeled = (
          e: React.MouseEvent<HTMLButtonElement, MouseEvent>
        ) => {
          e.preventDefault();
          if (!isClicked) {
            buttonRef.current?.focus();
          }
          setIsClicked((prev) => !prev);
          if (onClick) onClick(setIsClicked);
        };

        return (
          <Container>
            <Subcontainer
              $disabled={disabled}
              ref={buttonRef}
              $variant={variant}
              $onError={(onError?.hasError && "hasError") || "noError"}
              role="button"
              onClick={handleOnClickButtonUnlabeled}
              className={isClicked ? "active" : ""}
              placeholder={placeholder}
              content={label ?? ""}
            >
              {label?.length > 0 ? (
                <p className="content">{label}</p>
              ) : (
                <p className="placeholder">
                  {placeholder}
                  {hasMandatoryStyle && (
                    <span className="mandatory">&nbsp;*</span>
                  )}
                </p>
              )}
              <UilAngleDown className="dropdown-arrow" />
            </Subcontainer>
            {onError && onError.hasError && <p>{onError.message}</p>}
          </Container>
        );
    }
  }, [label, onError, isClicked, selectedOrganization]);

  return handleRenderButton();
};

export default Button;
